import React from 'react';

import { numberWithCommas, parseNullableValue } from '@clearverify/utils';

import { BulkUploadTable } from 'src/components/bulkUpload';
import type { TableColumn } from 'src/components/table';

import type { ExtendedInvitedEmployerData } from '../types';

type EmployersUploadTableProps = {
  employers: ExtendedInvitedEmployerData[];
  loading: boolean;
  handleSaveChanges: () => Promise<void>;
  handleBack: () => void;
};

export const EmployersUploadTable: React.FC<EmployersUploadTableProps> = ({
  employers,
  loading,
  handleSaveChanges,
  handleBack,
}) => {
  return (
    <BulkUploadTable
      entitites={employers}
      columns={columns}
      invalidEntititesMessage={invalidEntititesMessage}
      loading={loading}
      handleSaveChanges={handleSaveChanges}
      handleBack={handleBack}
    />
  );
};

const invalidEntititesMessage =
  'There were some errors while processing the file provided. Some fields are either missing or have been inaccurately formatted. If you proceed with this upload, the Employers highlighted red will not be added.';

const columns: TableColumn<ExtendedInvitedEmployerData>[] = [
  {
    key: 'fein',
    title: 'FEIN',
    render: (_valueByDataPath, employer) => {
      return employer.fein;
    },
  },
  {
    key: 'feinRegisteredBusinessName',
    title: 'Registered Business Name',
    render: (_valueByDataPath, employer) => {
      return employer.feinRegisteredBusinessName;
    },
  },
  {
    key: 'feinCommercialName',
    title: 'Commercial Name',
    render: (_valueByDataPath, employer) => {
      return employer.feinCommercialName;
    },
  },
  {
    key: 'displayName',
    title: 'Display Name',
    render: (_valueByDataPath, employer) => {
      return employer.displayName;
    },
  },
  {
    key: 'industry',
    title: 'Industry',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(employer.industry);
    },
  },
  {
    key: 'corporateStructure',
    title: 'Corporate Structure',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(employer.corporateStructure);
    },
  },
  {
    key: 'taxYearEndDate',
    title: 'Tax Year End Date',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(employer.taxYearEndDate);
    },
  },
  {
    key: 'numberOfEmployees',
    title: 'Number of Employees',
    render: (_valueByDataPath, employer) => {
      return numberWithCommas(parseNullableValue(employer.numberOfEmployees));
    },
  },
  {
    key: 'hqAddress',
    title: 'HQ Address',
    render: (_valueByDataPath, employer) => {
      return employer.hqAddress;
    },
  },
  {
    key: 'hqCity',
    title: 'HQ City',
    render: (_valueByDataPath, employer) => {
      return employer.hqCity;
    },
  },
  {
    key: 'hqCounty',
    title: 'HQ County',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(employer.hqCounty);
    },
  },
  {
    key: 'hqState',
    title: 'HQ State',
    render: (_valueByDataPath, employer) => {
      return employer.hqState;
    },
  },
  {
    key: 'hqZipCode',
    title: 'HQ ZIP Code',
    render: (_valueByDataPath, employer) => {
      return employer.hqZipCode;
    },
  },
  {
    key: 'contactEmail',
    title: 'Contact Email',
    render: (_valueByDataPath, employer) => {
      return employer.contactEmail;
    },
  },
  {
    key: 'emailForInvitation',
    title: 'Email for Invitation',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(employer.emailForInvitation);
    },
  },
  {
    key: 'contactPhone',
    title: 'Contact Phone',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(employer.contactPhone);
    },
  },
];
